import React, { useState, useEffect, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, IconButton, LinearProgress, CircularProgress, Menu, MenuItem, Select, Tab, Tabs, TextField, Tooltip, Typography, useMediaQuery, useTheme, Autocomplete, Grid } from '@mui/material';
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import { FaArchive, FaPaperPlane, FaPlus, FaTrash } from 'react-icons/fa';
import { DataGridPro, GridToolbarDensitySelector, GridToolbarColumnsButton, GridToolbarContainer, GridColDef, getGridStringOperators, GridFilterModel, GridSortModel, GridToolbarExport, useGridApiRef, GridLinkOperator } from '@mui/x-data-grid-pro';
import { RiExpandUpDownLine } from 'react-icons/ri';
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import { AddRequestSignature, PartnerLicenseeRow } from '../components/AddEdit/AddRequestSignature';
import Swal from 'sweetalert2';
import { formatUtcToLocal } from '../helper/utils';
import { StyledDropzone } from '../components/Elements/Dropzone';
import JSZip from 'jszip';
import { PartnerType, initPartner } from '../models/PartnersModel';
import { LightBlueBtnColor, GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../helper/theme';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { Search, Clear, Refresh } from '@mui/icons-material';

const DateFormat = 'MM-DD-YYYY';
enum QueueTabNames {
    Active = 'ACTIVE',
    Archive = 'ARCHIVE',
}
const ALL_STATES_OPTION = '__ALL_STATES__';

export interface SignatureRequestModel {
    signatureRequestId: number;
    partnerId: number;
    partnerName: string;
    state: string;
    stateId: number;

    signerUserId: number;
    signerUserName: string;
    signerUserFirstName: string;
    signerUserLastName: string;

    dueDate: string;
    formType: string;
    signerName: string;
    
    sentOn: string;
    dateSigned: string;
    isSigned: boolean;
    sentBy: number;
    sentByUserName: string;
    sentByUserFirstName: string;
    sentByUserLastName: string;
    isWetSignature?: boolean;
    isElectronicSignature?: boolean;
    isDigitalSignature?: boolean;
    notarizationRequired: boolean;
    isDocsUploaded?: boolean;
};
export interface SignatureRequestDocumentModel {
    signatureRequestId: number;
    signatureRequestDocumentId: number;
    documentName: string;
    documentUrl: string;
    fileSizeKB: string;
    isSigned: boolean;
    dateSigned: string;
    createdByUserId: number;
    createdByUsername: string;
    createdByUserRole?: string;
    createdDate: string;
};
export interface StatesModel {
    stateId: number;
    stateName: string;
    postalAbbv: string;
};
export interface PartnerModel {
    partnersId: number;
    customerName: string;
};

export const ClientUserRoles: string[] = [
    'Customer User',
    'Client Admin',
    'Client Processors',
    'State Reporter',
];

export const RequestSignature: React.FC = () => {
    const { user } = useAuth();
    const token = user?.token;
    const userRoles: string[] = user?.userRoles?.split(",").map(role => role.trim()) || [];
    const IsUserClient = Boolean(ClientUserRoles.some(role => userRoles.includes(role)));
    const [loading, setLoading] = useState(false);
    const [viewLoadingId, setViewLoadingId] = useState<number | null>(null);
    const [viewDocumentLoadingUrl, setViewDocumentLoadingUrl] = useState<string | null>(null);
    const [partners, setPartners] = useState<PartnerType[]>([initPartner]);
    const [states, setStates] = useState<StatesModel[]>([{ stateId: 0, stateName: "", postalAbbv: "" }]);
    const [licenseeRows, setLicenseeRows] = useState<PartnerLicenseeRow[]>([]);
    const [loadingLicenseeInfo, setLoadingLicenseeInfo] = useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedRows, setSelectedRows] = useState<SignatureRequestModel[]>([]);
    const [signatureRequests, setSignatureRequests] = useState<SignatureRequestModel[]>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    /** MISC staff: archived queue vs active (clients always see active; API enforces). */
    const [queueTab, setQueueTab] = useState<QueueTabNames>(QueueTabNames.Active);
    /** Top filter strip: order Client → State → Sent By (maps to filterModel for server-side query). */
    const [filterStripClientId, setFilterStripClientId] = useState<number>(0);
    const [filterStripState, setFilterStripState] = useState(ALL_STATES_OPTION);
    const [filterStripSentBy, setFilterStripSentBy] = useState('');
    const [draftFilterStripClientId, setDraftFilterStripClientId] = useState<number>(0);
    const [draftFilterStripState, setDraftFilterStripState] = useState(ALL_STATES_OPTION);
    const [draftFilterStripSentBy, setDraftFilterStripSentBy] = useState('');

    const filterModel: GridFilterModel = useMemo(() => {
        const items: GridFilterModel['items'] = [];
        if (filterStripClientId > 0) {
            const selectedPartnerName = partners.find((p) => Number(p.partnersId) === filterStripClientId)?.customerName?.trim();
            if (selectedPartnerName) {
                items.push({ id: 'strip-client', columnField: 'partnerName', operatorValue: 'equals', value: selectedPartnerName });
            }
        }
        if (filterStripState.trim() && filterStripState !== ALL_STATES_OPTION) {
            items.push({ id: 'strip-state', columnField: 'state', operatorValue: 'equals', value: filterStripState.trim() });
        }
        if (filterStripSentBy.trim()) {
            items.push({ id: 'strip-sentby', columnField: 'sentByUserName', operatorValue: 'contains', value: filterStripSentBy.trim() });
        }
        return { items, linkOperator: 'and' as GridLinkOperator };
    }, [filterStripClientId, filterStripState, filterStripSentBy, partners]);
    const requestSignaturePartnerOptions = useMemo(
        () =>
            partners
                .filter((p) => Number(p.partnersId) > 0 && (p.customerName || '').trim() !== '')
                .sort((a, b) => a.customerName.localeCompare(b.customerName)),
        [partners]
    );
    const requestSignatureStateOptions = useMemo(
        () =>
            states
                .filter((s) => Number(s.stateId) > 0 && (s.stateName || '').trim() !== '')
                .sort((a, b) => a.stateName.localeCompare(b.stateName)),
        [states]
    );
    const theme = useTheme();
    const isTabletOrDown = useMediaQuery(theme.breakpoints.down('md'));
    const requestSignatureGridApiRef = useGridApiRef();

    const [addRequestSignatureOpen, setAddRequestSignatureOpen] = useState(false);

    const [documentList, setDocumentList] = useState<SignatureRequestDocumentModel[]>([]);
    const [documentListOpen, setDocumentListOpen] = useState(false);
    const [signatureRequestId, setSignatureRequestId] = useState(0);

    // Document viewer state
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(null);
    const [documentViewerFileName, setDocumentViewerFileName] = useState<string | null>(null);
    const [viewingDocument, setViewingDocument] = useState(false);

    useEffect(() => {
        getPartners();
        getStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchSignatureRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, filterModel, sortModel, queueTab]);

    const handleSignatureFilterSearch = () => {
        setFilterStripClientId(draftFilterStripClientId);
        setFilterStripState(draftFilterStripState);
        setFilterStripSentBy(draftFilterStripSentBy.trim());
        setCurrentPage(1);
    };

    const handleSignatureFilterClear = () => {
        setDraftFilterStripClientId(0);
        setDraftFilterStripState(ALL_STATES_OPTION);
        setDraftFilterStripSentBy('');
        setFilterStripClientId(0);
        setFilterStripState(ALL_STATES_OPTION);
        setFilterStripSentBy('');
        setCurrentPage(1);
    };

    const handleSignatureFilterRefresh = () => {
        window.location.reload();
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const signatureRequestIdParam = searchParams.get('signatureRequestId');
        const openAttachments = searchParams.get('openAttachments') === 'true';

        if (signatureRequestIdParam && openAttachments) {
            const id = parseInt(signatureRequestIdParam, 10);
            if (!isNaN(id) && id > 0) {
                window.history.replaceState({}, '', window.location.pathname);
                setSignatureRequestId(id);
                viewDocumentsAttached(id);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPartners = () => {
        setLoading(true);
        apiClient('/Partners/getPartners', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setPartners(responseJson.data);
        }).catch(error => {
            console.error("Error fetching partners:", error)
        }).finally(() => {
            setLoading(false);
        });
    };
    const getStates = () => {
        setLoading(true);
        apiClient('/States/getStates', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setStates(responseJson.data);
        }).catch(error => {
            console.error("Error fetching states:", error)
        }).finally(() => {
            setLoading(false);
        });
    };
    const getPartnerLincenseesInformation = (partnersId: number) => {
        if (partnersId <= 0) {
            setLicenseeRows([]);
            return;
        }
        setLoadingLicenseeInfo(true);
        apiClient(`/Partners/getPartnerLincenseesInformation?partnerId=${partnersId}`, {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setLicenseeRows(responseJson.data ?? []);
        }).catch(error => {
            console.error("Error fetching partner licensee information:", error);
            setLicenseeRows([]);
        }).finally(() => {
            setLoadingLicenseeInfo(false);
        });
    };
    const fetchSignatureRequests = async () => {
        setLoading(true);
        
        // Convert filterModel to the format expected by the API
        const filterModelJson = filterModel && filterModel.items && filterModel.items.length > 0 ? filterModel : null;
        
        // Get sort field and direction from sortModel
        const sortField = sortModel && sortModel.length > 0 ? sortModel[0].field || '' : '';
        const sortDirection = sortModel && sortModel.length > 0 ? sortModel[0].sort || '' : '';
        
        apiClient('/SignatureRequests/getAllSignatureRequests', {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
            body: JSON.stringify({ 
                pageSize: pageSize, 
                pageNumber: currentPage,
                field: sortField,
                sort: sortDirection,
                filterModel: filterModelJson,
                archivedQueue: !IsUserClient && queueTab === QueueTabNames.Archive,
            }),
        }).then((response: any) => {
            if (!response) {
                setSignatureRequests([]);
                setTotalCount(0);
                return;
            }
            const rows = Array.isArray(response?.data?.signatureRequests)
                ? response.data.signatureRequests
                : [];
            const count = Number(response?.data?.totalCount);
            // Server-side filtering handles IsUserClient filtering, so no client-side filtering needed
            setSignatureRequests(rows);
            setTotalCount(Number.isFinite(count) ? count : 0);
        }).catch((error: any) => {
            console.error('Error fetching signature requests:', error);
            setSignatureRequests([]);
            setTotalCount(0);
        }).finally(() => {
            setLoading(false);
        })
    };

    const addNewSignatureRequest = () => {
        setSelectedRows([]);
        setAddRequestSignatureOpen(true);
    };
    const handleCloseAddRequestSignature = (isReload: boolean = false) => {
        setAddRequestSignatureOpen(false);
        if(isReload) {
            // reload the signature requests
            fetchSignatureRequests();
        }
        setSelectedRows([]);
    };
    const deleteSelectedSignatureRequests = () => {
        if(selectedRows.length === 0) {
            Swal.fire({
                title: 'No Signature Requests Selected',
                text: 'Please select at least one signature request to delete',
                icon: 'error',
                showCancelButton: true,
                confirmButtonText: 'OK',
            });
            return;
        }
        Swal.fire({
            title: '<div style="color: white;">Are you sure?</div>',
            html: '<div style="color: white;">This will delete the selected signature requests</div>',
            showCancelButton: true,
            confirmButtonText: '<div style="color: white;">Yes, Delete</div>',
            cancelButtonText: '<div style="color: white;">No, Continue</div>',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                const signatureRequestIdList = selectedRows.map(row => row.signatureRequestId).join(',');
                // call api to delete the signature requests
                apiClient(`/SignatureRequests/deleteSignatureRequests?signatureRequestIdList=${signatureRequestIdList}`, {
                    method: 'DELETE',
                    headers: new Headers({
                        'Authorization': `Bearer ${token}`,
                    }),
                }).then((response) => {
                    if (!response) return;
                    if (response.status) {
                        // reload the signature requests
                        fetchSignatureRequests();
                        setSelectedRows([]);
                        Swal.fire({
                            title: 'Success',
                            text: 'The signature requests have been deleted successfully',
                            icon: 'success',
                            confirmButtonText: 'OK',
                        });
                    }
                }).catch((error) => {
                    console.error('Error deleting signature requests:', error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to delete signature requests',
                        icon: 'error',
                        showCancelButton: true,
                        confirmButtonText: 'OK',
                    });
                });
            }
        }).catch((error) => {
            console.error('Error deleting signature requests:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to delete signature requests',
                icon: 'error',
                showCancelButton: true,
                confirmButtonText: 'OK',
            });
        });
    };

    const archiveSelectedSignatureRequests = () => {
        if (selectedRows.length === 0) {
            Swal.fire({
                title: 'No Selection',
                text: 'Select at least one signature request to archive.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }
        Swal.fire({
            title: '<div style="color: white;">Archive selected?</div>',
            html: '<div style="color: white;">These requests will move to the Archive queue</div>',
            showCancelButton: true,
            confirmButtonText: '<div style="color: white;">Archive</div>',
            cancelButtonText: '<div style="color: white;">Cancel</div>',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (!result.isConfirmed) return;
            const signatureRequestIdList = selectedRows.map((row) => row.signatureRequestId).join(',');
            apiClient('/SignatureRequests/archiveSignatureRequests', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }),
                body: JSON.stringify({ signatureRequestIdList }),
            })
                .then((response) => {
                    if (!response?.status) return;
                    fetchSignatureRequests();
                    setSelectedRows([]);
                    Swal.fire({ title: 'Archived', text: 'Selected requests were archived.', icon: 'success', confirmButtonText: 'OK' });
                })
                .catch((error) => {
                    console.error('Error archiving signature requests:', error);
                    Swal.fire({ title: 'Error', text: 'Failed to archive signature requests.', icon: 'error', confirmButtonText: 'OK' });
                });
        });
    };

    const unarchiveSelectedSignatureRequests = () => {
        if (selectedRows.length === 0) {
            Swal.fire({
                title: 'No Selection',
                text: 'Select at least one signature request to unarchive.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }
        Swal.fire({
            title: '<div style="color: white;">Unarchive selected?</div>',
            html: '<div style="color: white;">These requests will return to the Open queue.</div>',
            showCancelButton: true,
            confirmButtonText: '<div style="color: white;">Unarchive</div>',
            cancelButtonText: '<div style="color: white;">Cancel</div>',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (!result.isConfirmed) return;
            const signatureRequestIdList = selectedRows.map((row) => row.signatureRequestId).join(',');
            apiClient('/SignatureRequests/unarchiveSignatureRequests', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }),
                body: JSON.stringify({ signatureRequestIdList }),
            })
                .then((response) => {
                    if (!response?.status) return;
                    fetchSignatureRequests();
                    setSelectedRows([]);
                    Swal.fire({ title: 'Unarchived', text: 'Selected requests were unarchived.', icon: 'success', confirmButtonText: 'OK' });
                })
                .catch((error) => {
                    console.error('Error restoring signature requests:', error);
                    Swal.fire({ title: 'Error', text: 'Failed to restore signature requests.', icon: 'error', confirmButtonText: 'OK' });
                });
        });
    };

    const viewDocumentsAttached = (signatureRequestId: number, openDialog: boolean = true) => {
        setViewLoadingId(signatureRequestId);
        setLoading(true);
        try {
            apiClient(`/SignatureRequests/getSignatureRequestDocumentsById/${signatureRequestId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            }).then((response) => {
                if (!response) {
                    setLoading(false);
                    setViewLoadingId(null);
                    return;
                }
                const { data } = response;
                // Store the document list
                setDocumentList(data.documents);
                
                // show the list dialog, even if multiple documents
                if(openDialog) {
                    setDocumentListOpen(true);
                    // Clear loader after dialog opens (small delay to ensure dialog is rendered)
                    setTimeout(() => {
                        setLoading(false);
                        setViewLoadingId(null);
                    }, 100);
                } else {
                    setLoading(false);
                    setViewLoadingId(null);
                }
            }).catch((error) => {
                console.error("Error fetching documents:", error);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to fetch documents',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                setViewLoadingId(null);
            });
        } catch (error) {
            console.error("Error fetching documents:", error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch documents',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            setLoading(false);
            setViewLoadingId(null);
        }
    };
    const uploadSignedDocument = (files: File[]) => {
        if(files.length === 0) {
            Swal.fire({
                title: 'No Files Selected',
                text: 'Please select at least one file to upload',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }
        Swal.fire({ 
            title: '<div style="color: white;">Are you sure?</div>',
            html: '<div style="color: white;">This will upload the selected files to MISC</div>',
            showCancelButton: true,
            confirmButtonText: '<div style="color: white;">Yes, Upload</div>',
            cancelButtonText: '<div style="color: white;">No, Continue</div>',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                const formData = new FormData();
                files.forEach(file => {
                    formData.append('files', file);
                });
                formData.append('signatureRequestId', signatureRequestId.toString());
                // call api to upload the files
                apiClient(`/SignatureRequests/uploadSignedDocument`, {
                    method: 'POST',
                    headers: new Headers({
                        'Authorization': `Bearer ${token}`,
                    }),
                    body: formData,
                }).then((response) => {
                    if (!response) return;
                    if (response.status) {
                        // reload the signature requests
                        fetchSignatureRequests();
                        // setSelectedRows([]);
                        handleCloseDocumentList();
                        Swal.fire({
                            title: 'Success',
                            text: 'The files have been uploaded successfully',
                            icon: 'success',
                            confirmButtonText: 'OK',
                        });
                    }
                }).catch((error) => {
                    console.error('Error uploading files:', error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to upload files',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                });
            }
        }).catch((error) => {
            console.error('Error uploading files:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to upload files',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        });
    };

    const downloadRequestDocumentListToZip = async (signatureRequestId: number) => {
        setLoading(true);
        
        // Show loading dialog while fetching documents
        Swal.fire({
            title: 'Loading Documents...',
            html: 'Please wait while we load the documents.',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            // Fetch documents directly
            const response = await apiClient(`/SignatureRequests/getSignatureRequestDocumentsById/${signatureRequestId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            });

            if (!response || !response.data || !response.data.documents) {
                Swal.close();
                Swal.fire({
                    title: 'No Documents Found',
                    text: 'There are no documents available to download for this signature request.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const documentsToDownload: SignatureRequestDocumentModel[] = response.data.documents;
            
            if(documentsToDownload.length === 0) {
                Swal.close();
                Swal.fire({
                    title: 'No Documents Available',
                    text: 'There are no documents available to download.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const selectedSignatureRequest = signatureRequests.find(request => request.signatureRequestId === signatureRequestId);
            const folderName = `${selectedSignatureRequest?.signerUserName} - ${selectedSignatureRequest?.formType} - ${selectedSignatureRequest?.partnerName}`;
            
            Swal.close();
            
            const confirmResult = await Swal.fire({
                title: '<div style="color: white;">Download Documents</div>',
                html: `<div style="color: white;">This will download all documents to a zip file named <strong>${folderName}.zip</strong></div>`,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                background: PrimaryBlueColor,
            });
            
            if (!confirmResult.isConfirmed) {
                setLoading(false);
                return;
            }
            
            // Show loading dialog for download preparation
            Swal.fire({
                title: 'Preparing Download...',
                html: 'Please wait while we prepare your files for download.',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });
            
            const zip = new JSZip();
            let successCount = 0;
            let failCount = 0;
            const errors: string[] = [];
            const usedFileNames = new Set<string>();
            
            // Helper function to generate unique filename (Chrome-style numbering)
            const generateUniqueFileName = (baseFileName: string): string => {
                const fileExtension = baseFileName.includes('.') 
                    ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                    : '';
                const fileNameWithoutExt = baseFileName.includes('.')
                    ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                    : baseFileName;
                
                let uniqueFileName = baseFileName;
                let counter = 0;
                
                // Keep trying until we find a unique name
                while (usedFileNames.has(uniqueFileName)) {
                    counter++;
                    uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                }
                
                usedFileNames.add(uniqueFileName);
                return uniqueFileName;
            };
            
            // Generate unique filenames for all files first
            const filesWithUniqueNames = documentsToDownload.map((document: SignatureRequestDocumentModel) => ({
                document,
                uniqueFileName: generateUniqueFileName(document.documentName || `document_${document.signatureRequestDocumentId}`)
            }));
            
            // Fetch each file and add to zip
            await Promise.all(
                filesWithUniqueNames.map(async ({ document, uniqueFileName }) => {
                    try {
                        // Fetch the file from documentUrl
                        const response = await fetch(document.documentUrl, {
                            method: 'GET',
                            mode: 'cors',
                            credentials: 'omit',
                            cache: 'no-cache',
                        });

                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }

                        // Get the blob (preserves original content type)
                        const blob = await response.blob();
                        
                        // Add file to zip
                        zip.file(uniqueFileName, blob);
                        successCount++;
                    } catch (error: any) {
                        failCount++;
                        const errorMsg = error.message || 'Failed to download';
                        errors.push(`${document.documentName}: ${errorMsg}`);
                        console.error(`Error downloading ${document.documentName} from ${document.documentUrl}:`, error);
                        console.error('Error details:', error);
                    }
                })
            );

            if (successCount === 0) {
                Swal.close();
                throw new Error('Failed to download any documents');
            }

            // Generate zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            
            // Create download link
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${folderName}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            Swal.close();
            
            // Show success/partial success message
            if (failCount > 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Partial Success',
                    html: `
                        <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                        <p><strong>${failCount}</strong> document(s) failed to download.</p>
                        ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                    `,
                    confirmButtonText: 'OK',
                });
            } else {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `All ${successCount} document(s) have been downloaded successfully.`,
                    confirmButtonText: 'OK',
                });
            }
        } catch (error: any) {
            console.error('Error in downloadRequestDocumentListToZip:', error);
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
        } finally {
            setLoading(false);
        }
    };

    const filterOperators = getGridStringOperators().filter(({ value }) => ['contains'].includes(value));
    const columns: GridColDef[] = [
        { field: 'Action', headerName: 'Action', flex: 0.8, minWidth: 160, maxWidth: 160, filterable: false, hideSortIcons: true,
            sortable: false,
            disableColumnMenu: true,
            headerAlign: 'left', align: 'left', disableExport: true,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'center' }}>
                    <Tooltip title={IsUserClient ? "View & Upload Documents Attached" : "View Documents Attached"} arrow={true} placement="top">
                        <IconButton 
                            sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
                            disabled={viewLoadingId === params.row.signatureRequestId}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.signatureRequestId) {
                                    setSignatureRequestId(params.row.signatureRequestId);
                                    viewDocumentsAttached(params.row.signatureRequestId);
                                }
                            }}
                        >
                            {viewLoadingId === params.row.signatureRequestId ? (
                                <CircularProgress size={20} sx={{ color: LightBlueBtnColor }} />
                            ) : (
                                // <FaRegEye />
                                <RemoveRedEyeOutlinedIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                    {/* <Tooltip title="Upload Documents" arrow={true}>
                        <IconButton sx={{ color: LightBlueBtnColor }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.signatureRequestId) {
                                    setSignatureRequestId(params.row.signatureRequestId);
                                    viewDocumentsAttached(params.row.signatureRequestId);
                                }
                            }}
                        ><FaUpload /></IconButton>
                    </Tooltip> */}
                    <Tooltip title="Download Documents" arrow={true} placement="top">
                        <IconButton sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.signatureRequestId) {
                                    downloadRequestDocumentListToZip(params.row.signatureRequestId);
                                }
                            }}
                        ><FileDownloadOutlinedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                </Box>
            ) 
        },
        { field: 'dueDate', headerName: 'Due Date',
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'dueDate'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                    <div>
                        <span className="table-card-title">{params.colDef.headerName}</span>
                        {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                    </div>
                )
            },
            type: 'string',
            flex: 0.5, minWidth: 110, align: 'left', headerAlign: 'left', filterOperators,
            renderCell: (params) => (
                <div>{params.value ? formatUtcToLocal(params.value, 'MM/DD/YYYY') : ''}</div>
            )
        },
        { field: 'state', headerName: 'State', 
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'state'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                    <div>
                        <span className="table-card-title">{params.colDef.headerName}</span>
                        {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                    </div>
                )
            }, type: 'string', 
            flex: 0.5, minWidth: 90, align: 'left', headerAlign: 'left', filterOperators 
        },
        { field: 'formType', headerName: 'Form Type', 
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'formType'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                    <div>
                        <span className="table-card-title">{params.colDef.headerName}</span>
                        {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                    </div>
                )
            }, type: 'string', 
            flex: 0.5, minWidth: 120, align: 'left', headerAlign: 'left', filterOperators 
        },
        { field: 'signerUserName', headerName: 'Signer', 
            valueGetter: (params) => params.row.signerUserName ?? '',
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'signerUserName'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                        {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )
            }, type: 'string', 
            flex: 0.5, minWidth: 100, align: 'left', headerAlign: 'left', filterOperators 
        },
        { field: 'partnerName', headerName: 'Client', 
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'partnerName'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                        {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                    </div>
                )
            }, type: 'string', 
            flex: 0.5, minWidth: 100, align: 'left', headerAlign: 'left', filterOperators 
        },
        { field: 'sentOn', headerName: 'Sent On', 
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'sentOn'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                        {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )
            }, type: 'string', 
            flex: 0.5, minWidth: 100, align: 'left', headerAlign: 'left', filterOperators,
            renderCell: (params) => (
                <div>{formatUtcToLocal(params.value, DateFormat)}</div>
            )
        },
        { field: 'sentByUserName', headerName: 'Sent By', 
            valueGetter: (params) => `${params.row.sentByUserName ?? ''}`,
            hideSortIcons: true,
            renderHeader: (params) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'sentByUserName'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                        {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )
        }, type: 'string', 
        flex: 0.5, minWidth: 100, align: 'left', headerAlign: 'left', filterOperators,
            renderCell: (params) => (
                <div>{params.value === 'miscstaff' ? 'MISC team' : (params.value || '-')}</div>
            )
        },
        ...(!IsUserClient ? [{ 
            field: 'dateSigned', headerName: 'Date Signed', 
            hideSortIcons: true,
            renderHeader: (params: any) => {
                const sortItem = sortModel.find(
                    (item: any) => item.field === 'dateSigned'
                  );
            
                const isSortedAsc = sortItem?.sort === 'asc';
                const isSortedDesc = sortItem?.sort === 'desc'
                return (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                        {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
                </div>
            )
            }, type: 'string', 
            flex: 0.5, minWidth: 130, align: 'left' as const, headerAlign: 'left' as const, filterOperators, 
            renderCell: (params: any) => (
                <div>
                    {params.row.isSigned ? formatUtcToLocal(params.row.dateSigned, DateFormat) : 'N/A'}
                </div>
            )
        },] : []),
        
    ];
    
    const handleCloseDocumentList = () => {
        setDocumentListOpen(false);
        setSelectedRows([]);
        setDocumentList([]);
        if (window.location.search) {
            window.history.replaceState({}, '', window.location.pathname);
        }
    };

    // Document viewer handlers
    const viewDocument = (documentUrl: string, documentName: string) => {
        setViewDocumentLoadingUrl(documentUrl);
        setViewingDocument(true);
        setDocumentViewerUrl(documentUrl);
        setDocumentViewerFileName(documentName);
        setDocumentViewerOpen(true);
        // Simulate loading delay if needed
        setTimeout(() => {
            setViewingDocument(false);
            setViewDocumentLoadingUrl(null);
        }, 500);
    };

    const handleCloseDocumentViewer = () => {
        setDocumentViewerOpen(false);
        setDocumentViewerUrl(null);
        setDocumentViewerFileName(null);
        setViewingDocument(false);
    };
    
    //#region UI Elements
    const getRowsPerPageOptions = (totalCount: number, pageSize: number) => {
        return [10, 25, 50, 100];
    };
    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton className="me-3 sky-blue" />
                <GridToolbarExport className="me-3 sky-blue" />
                <GridToolbarDensitySelector className="me-3 sky-blue" />
            </GridToolbarContainer>
        );
    };
    function EditToolbar() {
        const [toolbarMenuAnchor, setToolbarMenuAnchor] = useState<null | HTMLElement>(null);
        const openToolbarMenu = Boolean(toolbarMenuAnchor);

        const handleToolbarMenuClose = () => setToolbarMenuAnchor(null);
        const handleToolbarAction = (action: 'columns' | 'exportCsv' | 'exportPrint') => {
            handleToolbarMenuClose();
            const api = requestSignatureGridApiRef.current;
            if (!api) return;
            if (action === 'columns') (api as any).showPreferences('columns');
            else if (action === 'exportCsv') api.exportDataAsCsv();
            else if (action === 'exportPrint') api.exportDataAsPrint();
        };

        return (<>
            <div className="p-2 d-flex justify-content-end datagrid-butons">
                {!IsUserClient && (
                    <>
                        <Tabs
                            value={queueTab}
                            onChange={(_, next) => {
                                const nextTab = next as QueueTabNames;
                                if (nextTab === queueTab) return;
                                setQueueTab(nextTab);
                                setCurrentPage(1);
                                setSelectedRows([]);
                            }}
                            aria-label="Signature request tabs"
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                minHeight: 48,
                                marginRight: 2,
                                '& .MuiButtonBase-root': { minHeight: '0px' },
                                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem' },
                                '& .Mui-selected': { color: '#2099d2' },
                                '& .MuiTabs-indicator': { backgroundColor: '#2099d2', height: 3, borderRadius: '3px 3px 0 0' },
                            }}
                        >
                            <Tab label="Open" value={QueueTabNames.Active} icon={<FaPaperPlane fontSize="small" />} iconPosition="start" />
                            <Tab label="Archived" value={QueueTabNames.Archive} icon={<FaArchive fontSize="small" />} iconPosition="start" />
                        </Tabs>
                        <FormControl
                            size="small"
                            sx={{
                                display: { xs: 'block', md: 'none' },
                                minWidth: 160,
                                marginRight: 2,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                                '& .MuiSelect-select': { fontWeight: 600, color: '#2099d2', py: 0.9 },
                            }}
                        >
                            <Select
                                value={queueTab}
                                displayEmpty
                                sx={{ textTransform: 'none', fontSize: '0.9375rem' }}
                                onChange={(e) => {
                                    const next = e.target.value as QueueTabNames;
                                    if (next === queueTab) return;
                                    setQueueTab(next);
                                    setCurrentPage(1);
                                    setSelectedRows([]);
                                }}
                            >
                                <MenuItem value={QueueTabNames.Active}>Open</MenuItem>
                                <MenuItem value={QueueTabNames.Archive}>Archived</MenuItem>
                            </Select>
                        </FormControl>
                    </>
                )}
                <Box className='ms-auto' sx={{ display: 'flex', alignItems: 'center' }}>
                    {isTabletOrDown ? (
                        <>
                            <IconButton
                                aria-label="Table options"
                                aria-controls={openToolbarMenu ? 'table-options-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={openToolbarMenu ? 'true' : undefined}
                                onClick={(e) => setToolbarMenuAnchor(e.currentTarget)}
                                sx={{ color: '#2099d2' }}
                            >
                                <MoreVertOutlinedIcon />
                            </IconButton>
                            <Menu
                                id="table-options-menu"
                                anchorEl={toolbarMenuAnchor}
                                open={openToolbarMenu}
                                onClose={handleToolbarMenuClose}
                                MenuListProps={{ 'aria-labelledby': 'table-options-button' }}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <MenuItem onClick={() => handleToolbarAction('columns')}>Columns</MenuItem>
                                <MenuItem onClick={() => handleToolbarAction('exportCsv')}>Download as CSV</MenuItem>
                                <MenuItem onClick={() => handleToolbarAction('exportPrint')}>Print</MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <CustomToolbar />
                    )}
                </Box>
            </div>
        </>);
    };
    //#endregion
    
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ position: "fixed", top: "0", left: "0", zIndex: 99999, width: "100%", pointerEvents: "none" }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
            </div>
            <Box sx={{ width: '100%' }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        backgroundColor: "#fafbfc",
                        minHeight: 56,
                        marginLeft: { xs: '-24px', md: '0px' },
                        marginRight: { xs: '-24px', md: '0px' },
                    }}
                >
                    <h4 className="mb-0 py-2" style={{ fontWeight: 600, color: "#0f172a" }}>I Want To... Request a Signature</h4>
                
                    {!IsUserClient && (
                        <Box sx={{ width: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={addNewSignatureRequest}
                                startIcon={<FaPlus fontSize="small" />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    backgroundColor: LightBlueBtnColor,
                                    '&:hover': { backgroundColor: '#176990' },
                                    px: 2,
                                }}
                            >
                                New Request
                            </Button>
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={deleteSelectedSignatureRequests}
                                startIcon={<FaTrash fontSize="small" />}
                                disabled={selectedRows.length === 0}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    backgroundColor: '#dc2626',
                                    '&:hover': { backgroundColor: '#b91c1c' },
                                    px: 2,
                                }}
                            >
                                Delete
                            </Button>
                            {queueTab !== QueueTabNames.Archive ? (
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={archiveSelectedSignatureRequests}
                                    startIcon={<FaArchive fontSize="small" />}
                                    disabled={selectedRows.length === 0}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backgroundColor: '#64748b',
                                        '&:hover': { backgroundColor: '#475569' },
                                        px: 2,
                                    }}
                                >
                                    Archive
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={unarchiveSelectedSignatureRequests}
                                    startIcon={<FaArchive fontSize="small" />}
                                    disabled={selectedRows.length === 0}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backgroundColor: '#64748b',
                                        '&:hover': { backgroundColor: '#475569' },
                                        px: 2,
                                    }}
                                >
                                    Unarchive
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Signature Requests Table */}
                <Box sx={{ width: '100%', py: 2, px: 2 }}>
                    <Grid container spacing={2} className="filter-grid" display="flex">
                                <Grid item xl={2} lg={4} sm={6} xs={12}>
                                    <FormControl fullWidth>
                                <Autocomplete
                                    sx={{
                                        marginTop: '-4px !important',
                                        '& .MuiAutocomplete-input': { padding: '0px 4px 0px 5px !important' },
                                    }}
                                    options={requestSignaturePartnerOptions}
                                    getOptionLabel={(option) => option.customerName}
                                    isOptionEqualToValue={(a, b) => Number(a.partnersId) === Number(b.partnersId)}
                                    value={requestSignaturePartnerOptions.find((p) => Number(p.partnersId) === draftFilterStripClientId) ?? null}
                                    onChange={(_, v) => setDraftFilterStripClientId(v ? Number(v.partnersId) : 0)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Client"
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { marginTop: '5px !important' },
                                                '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                                                '& .MuiInputLabel-root': { transform: 'translate(14px, 12px) scale(1)' },
                                                '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
                                            }}
                                        />
                                    )}
                                />
                                    </FormControl>
                                </Grid>
                                <Grid item xl={2} lg={4} sm={6} xs={12}>
                                    <FormControl fullWidth>
                                <Autocomplete
                                    sx={{
                                        marginTop: '-4px !important',
                                        '& .MuiAutocomplete-input': { padding: '0px 4px 0px 5px !important' },
                                    }}
                                    options={requestSignatureStateOptions}
                                    getOptionLabel={(option) => option.stateName}
                                    isOptionEqualToValue={(a, b) => a.stateId === b.stateId}
                                    value={requestSignatureStateOptions.find((s) => s.stateName === draftFilterStripState) ?? null}
                                    onChange={(_, v) => setDraftFilterStripState(v?.stateName ?? ALL_STATES_OPTION)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="State"
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { marginTop: '5px !important' },
                                                '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                                                '& .MuiInputLabel-root': { transform: 'translate(14px, 12px) scale(1)' },
                                                '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
                                            }}
                                        />
                                    )}
                                />
                                    </FormControl>
                                </Grid>
                                <Grid item xl={2} lg={4} sm={6} xs={12}>
                                    <TextField
                                size="small"
                                label="Sent By"
                                value={draftFilterStripSentBy}
                                onChange={(e) => setDraftFilterStripSentBy(e.target.value)}
                                placeholder="Contains…"
                                sx={{
                                    minWidth: { xs: '100%', sm: 180 },
                                    flex: { sm: '0 1 220px' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' },
                                    '& .MuiInputBase-root': { marginTop: '1px !important' },
                                    '& .MuiInputBase-input': { padding: '6px 14px !important' },
                                }}
                                fullWidth
                                inputProps={{ 'aria-label': 'Filter by sent by' }}
                            />
                                </Grid>
                                <Grid item xs={12} sm="auto">
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={handleSignatureFilterSearch}
                                    startIcon={<Search />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backgroundColor: '#2099d2',
                                        '&:hover': { backgroundColor: '#176990' },
                                    }}
                                >
                                    Search
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={handleSignatureFilterClear}
                                    startIcon={<Clear />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: '#e2e8f0',
                                        color: '#475569',
                                        '&:hover': { borderColor: '#2099d2', color: '#2099d2' },
                                    }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={handleSignatureFilterRefresh}
                                    startIcon={<Refresh />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: '#e2e8f0',
                                        color: '#475569',
                                        '&:hover': {
                                            borderColor: '#2099d2',
                                            backgroundColor: '#2099d214',
                                            color: '#2099d2',
                                        },
                                    }}
                                >
                                    Refresh
                                </Button>
                                    </Box>
                                </Grid>
                    </Grid>
                </Box>
                <Box sx={{ width: "100%" }}>
                    <div
                        style={{
                            height: "calc(100vh - 300px)",
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            borderRadius: "12px",
                            overflow: "hidden",
                            boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            position: "relative",
                        }}
                    >
                        {loading && (
                            <Box
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    backgroundColor: "rgba(250, 251, 252, 0.85)",
                                    zIndex: 10,
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 1,
                                }}
                            >
                                <Box sx={{ width: "100%", maxWidth: 320 }}>
                                    <LinearProgress color="info" sx={{ backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#132D3D" } }} />
                                    <Box sx={{ mt: 1.5, textAlign: "center" }}>
                                        <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>Loading...</span>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                            <div style={{ flex: '1 1 0%', overflow: 'auto' }}>
                            <DataGridPro
                                apiRef={requestSignatureGridApiRef}
                                rows={signatureRequests}
                                getRowId={(r) => r?.signatureRequestId ?? 0}
                                columns={columns}

                                page={currentPage - 1}
                                pageSize={pageSize}
                                pagination
                                rowCount={totalCount}
                                paginationMode="server"
                                onPageChange={(newPage) => setCurrentPage(newPage + 1)}
                                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                                rowsPerPageOptions={getRowsPerPageOptions(totalCount, pageSize)}
                                filterMode="server"
                                filterModel={filterModel}
                                disableColumnFilter
                                onFilterModelChange={() => {
                                    /* Filters come from the Client → State → Sent By strip only */
                                }}
                                sortingMode="server"
                                sortingOrder={['asc', 'desc']}
                                sortModel={sortModel}
                                onSortModelChange={(newSortModel) => {
                                    setSortModel(newSortModel);
                                    setCurrentPage(1); // Reset to first page when sort changes
                                }}
                                loading={loading}
                                checkboxSelection={!IsUserClient ? true : false}
                                selectionModel={selectedRows.map(row => row.signatureRequestId).filter((id): id is number => id !== undefined)}
                                onSelectionModelChange={(newSelectionModel) => {
                                    // Map the selected IDs to full ReportTagModel objects
                                    const selectedObjects = (newSelectionModel as number[])
                                        .map(id => signatureRequests.find(request => request.signatureRequestId === id))
                                        .filter((request): request is SignatureRequestModel => request !== undefined);
                                    setSelectedRows(selectedObjects);
                                }}
                                
                                components={{
                                    Toolbar: EditToolbar,
                                }}
                                sx={{
                                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                    border: 'none',
                                    fontSize: '14px',
                                    color: '#475569',
                                    backgroundColor: '#ffffff',
                                    "& .MuiDataGrid-columnHeaderTitle": {
                                        whiteSpace: 'normal',
                                        lineHeight: 1.35,
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        letterSpacing: '0.05em',
                                        color: '#162E3C',
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f8fafc',
                                        borderTop: '2px solid #e2e8f0',
                                        borderBottom: '2px solid #e2e8f0',
                                        minHeight: '52px !important',
                                        maxHeight: '52px !important',
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        '&:focus, &:focus-within': { outline: 'none' },
                                    },
                                    '& .MuiDataGrid-row': {
                                        transition: 'background-color 0.15s ease',
                                        '&:nth-of-type(even)': {
                                            backgroundColor: '#fafbfc',
                                        },
                                        '&:hover': {
                                            backgroundColor: '#f1f5f9 !important',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: '#ebf5fa !important',
                                            '&:hover': {
                                                backgroundColor: '#dbeef7 !important',
                                            },
                                        },
                                    },
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: '1px solid #f1f5f9',
                                        fontSize: '14px',
                                        color: '#334155',
                                        '&:focus': { outline: 'none' },
                                    },
                                    ".MuiDataGrid-virtualScroller": {
                                        border: 'none',
                                    },
                                    "& .MuiDataGrid-main": {
                                        border: 'none',
                                    },
                                    "& .MuiDataGrid-footerContainer": {
                                        borderTop: '1px solid #e2e8f0',
                                        backgroundColor: '#f8fafc',
                                        minHeight: 56,
                                    },
                                    "& .MuiTablePagination-root": {
                                        color: '#64748b',
                                        fontSize: '13px',
                                    },
                                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                        marginBottom: 0,
                                    },
                                    "& .MuiTablePagination-select": {
                                        fontSize: '13px',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                    },
                                    "& .MuiIconButton-root": {
                                        color: '#64748b',
                                        "&:hover": {
                                            backgroundColor: '#f1f5f9',
                                            color: '#475569',
                                        },
                                    },
                                    "& .MuiCheckbox-root": {
                                        color: '#94a3b8',
                                        "&.Mui-checked": {
                                            color: '#2099d2',
                                        },
                                    },
                                }}
                            />
                            </div>
                    </div>
                </Box>
                
            </Box>

            <Box sx={{ width: '80%' }}>
                <AddRequestSignature Open={addRequestSignatureOpen} 
                    handleClose={handleCloseAddRequestSignature} 
                    partners={partners} 
                    licenseeRows={licenseeRows}
                    states={states}
                    loadingLicenseeInfo={loadingLicenseeInfo}
                    onClientChange={(partnersId) => getPartnerLincenseesInformation(partnersId)}
                />
            </Box>

            <Box sx={{ width: '80%' }}>
                <ViewAttachments open={documentListOpen} handleClose={handleCloseDocumentList} 
                    documentList={documentList}
                    loading={loading}
                    IsUserClient={IsUserClient}
                    uploadSignedDocument={uploadSignedDocument}
                    downloadRequestDocumentListToZip={downloadRequestDocumentListToZip}
                    viewDocument={viewDocument}
                    viewDocumentLoadingUrl={viewDocumentLoadingUrl}
                />
            </Box>

            {/* Document Viewer Dialog */}
            <Dialog open={documentViewerOpen} 
                onClose={handleCloseDocumentViewer}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    style: {
                        maxHeight: "90vh",
                    },
                }}
            >
                <DialogTitle className="card-title">
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                    }}>
                        <Box sx={{
                            flex: 1,
                            fontWeight: "medium",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            paddingRight: "16px",
                        }}>
                            {documentViewerFileName || "Document Viewer"}
                        </Box>
                        <IconButton onClick={handleCloseDocumentViewer}
                            sx={{
                                color: "#666",
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                    color: "#000",
                                },
                            }}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ padding: 0, height: "80vh", position: "relative" }}>
                    {viewingDocument ? (
                        <Box sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                        }}>
                            <CircularProgress />
                        </Box>
                    ) : documentViewerUrl ? (
                        <Box sx={{ width: "100%", height: "100%" }}>
                            <iframe title="Document Viewer"
                                src={documentViewerUrl}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                }}
                            />
                        </Box>
                    ) : null}
                </DialogContent>
            </Dialog>
        </LocalizationProvider>
    );
};

interface ViewAttachmentsProps {
    open: boolean;
    handleClose: () => void;
    documentList: SignatureRequestDocumentModel[];
    loading: boolean;
    IsUserClient: boolean;
    uploadSignedDocument: (files: File[]) => void;
    downloadRequestDocumentListToZip: (signatureRequestId: number) => void;
    viewDocument: (documentUrl: string, documentName: string) => void;
    viewDocumentLoadingUrl: string | null;
};
const ViewAttachments: React.FC<ViewAttachmentsProps> = ({ open, handleClose, documentList, loading, IsUserClient, uploadSignedDocument, downloadRequestDocumentListToZip, viewDocument, viewDocumentLoadingUrl }) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    
    const closeAttachmentsDialog = () => {
        handleClose();
        setSelectedFiles([]);
    };
    
    const columns: GridColDef[] = [
        { field: 'documentName', headerName: 'Document Name', 
            flex: 2, minWidth: 200, align: 'left', headerAlign: 'left', type: 'string', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
        },
        { field: 'createdByUsername', headerName: 'Sent By', 
            flex: 1.5, minWidth: 120, align: 'left', headerAlign: 'left', type: 'string', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
            renderCell: (params) => (
                <div>
                    {params.value ?? 'N/A'}
                </div>
            )
        },
        { field: 'createdDate', headerName: 'Date Uploaded', 
            flex: 0.8, minWidth: 130, align: 'left', headerAlign: 'left', type: 'date', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
            renderCell: (params) => (
                <div>
                    {formatUtcToLocal(params.value, DateFormat)}
                </div>
            )
        },
        { field: 'action', headerName: 'Action',
            hideSortIcons: true,
            type: 'string', 
            flex: 0.6, minWidth: 120, align: 'left', headerAlign: 'left', 
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'left', gap: 1.5, justifyContent: 'left' }}>
                    <Tooltip title="View Document" arrow={true}>
                        <IconButton 
                            sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
                            disabled={viewDocumentLoadingUrl === params.row.documentUrl}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.documentUrl && params.row.documentName) {
                                    viewDocument(params.row.documentUrl, params.row.documentName);
                                }
                            }} 
                        >
                            {viewDocumentLoadingUrl === params.row.documentUrl ? (
                                <CircularProgress size={20} sx={{ color: LightBlueBtnColor }} />
                            ) : (
                                // <FaRegEye />
                                <RemoveRedEyeOutlinedIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Download Documents" arrow={true}>
                        <IconButton sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.signatureRequestId) {
                                    downloadRequestDocumentListToZip(params.row.signatureRequestId);
                                }
                            }}
                        ><FileDownloadOutlinedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                </Box>
            )
        },
    ];
    
    return (
        <Dialog open={open} onClose={closeAttachmentsDialog} maxWidth="lg" fullWidth>
            <DialogTitle
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 2,
                    px: 3,
                    background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FilePresentOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
                <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>{IsUserClient ? 'View & Upload Attachments' : 'View Attachments'}</Typography>
                </Box>
                <IconButton onClick={closeAttachmentsDialog}
                    sx={{ color: "#ffffff" }}
                    aria-label="close"
                >
                    <CloseOutlinedIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: '#ffffff', padding: '16px !important', margin: 0 }}>
                <Box overflow={"scroll"}>
                    <Box sx={{ p: 3 }}>
                        <div style={{ height: "59vh", display: "flex", flexDirection: "column", width: "100%" }}>
                            {loading && <LinearProgress sx={{ backgroundColor: "#132D3D" }} />}
                            <div style={{ flex: 1, overflow: "auto" }}>
                                <DataGridPro rows={documentList}
                                    getRowId={(r) => r?.signatureRequestDocumentId ?? 0}
                                    columns={columns}
                                    getRowHeight={() => 52}
                                    sx={{
                                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                        border: 'none',
                                        fontSize: '14px',
                                        color: '#475569',
                                        backgroundColor: '#ffffff',
                                        "& .MuiDataGridPro-columnHeaderTitle": {
                                            whiteSpace: "normal",
                                            lineHeight: 1.35,
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            letterSpacing: "0.05em",
                                            textTransform: "uppercase",
                                            color: "#64748b",
                                        },
                                        "& .MuiDataGrid-columnHeaders": {
                                            backgroundColor: "#f8fafc",
                                            borderBottom: "2px solid #e2e8f0",
                                            borderTop: "2px solid #e2e8f0",
                                            minHeight: "52px !important",
                                            maxHeight: "52px !important",
                                        },
                                        '& .MuiDataGrid-columnHeader': {
                                            '&:focus, &:focus-within': { outline: 'none' },
                                        },
                                        '& .MuiDataGrid-row': {
                                            transition: 'background-color 0.15s ease',
                                            '&:nth-of-type(even)': {
                                                backgroundColor: '#fafbfc',
                                            },
                                            '&:hover': {
                                                backgroundColor: '#f1f5f9 !important',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#ebf5fa !important',
                                                '&:hover': {
                                                    backgroundColor: '#dbeef7 !important',
                                                },
                                            },
                                        },
                                        '& .MuiDataGrid-cell': {
                                            borderBottom: '1px solid #f1f5f9',
                                            fontSize: '14px',
                                            color: '#334155',
                                            '&:focus': { outline: 'none' },
                                        },
                                        ".MuiDataGrid-virtualScroller": {
                                            border: 'none',
                                        },
                                        "& .MuiDataGrid-main": {
                                            border: 'none',
                                        },
                                        "& .MuiDataGrid-footerContainer": {
                                            borderTop: '1px solid #e2e8f0',
                                            backgroundColor: '#f8fafc',
                                            minHeight: 56,
                                        },
                                        "& .MuiTablePagination-root": {
                                            color: '#64748b',
                                            fontSize: '13px',
                                        },
                                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                            marginBottom: 0,
                                        },
                                        "& .MuiTablePagination-select": {
                                            fontSize: '13px',
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                        },
                                        "& .MuiIconButton-root": {
                                            color: '#64748b',
                                            "&:hover": {
                                                backgroundColor: '#f1f5f9',
                                                color: '#475569',
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </Box>
                </Box>
                
                {/* upload documents area for client users */}
                {IsUserClient && (
                <Box sx={{  }}>
                    <DialogContent>
                        <DialogTitle className="card-subtitle"
                            sx={{
                                textAlign: 'center',
                            }}
                        >Upload a signed document and send to MISC</DialogTitle>
                        <FormHelperText className='formtexthelper-heading'>Upload Documents</FormHelperText>
                        <StyledDropzone label="Upload a signed document and send to MISC"
                            onFileDrop={(files: File[]) => {
                                setSelectedFiles([...selectedFiles, ...files]);
                            }}
                        />
                        {selectedFiles && selectedFiles.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <FormHelperText className='formtexthelper-heading'>
                                    Selected Files ({selectedFiles.length}):
                                </FormHelperText>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {selectedFiles.map((file, index) => (
                                        <Chip key={index} label={file.name} onDelete={() => {
                                            setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
                                        }} deleteIcon={<CancelIcon />} color="primary" variant="outlined" />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                </Box>
                )}
            </DialogContent>
            <DialogActions>
                {/* <Button
                    variant="outlined"
                    size="medium"
                    onClick={closeAttachmentsDialog}
                    startIcon={<CloseOutlinedIcon />}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#e2e8f0",
                        color: "#475569",
                        "&:hover": {
                        borderColor: "#2099d2",
                        backgroundColor: "#2099d214",
                        color: "#2099d2",
                        },
                    }}
                    >Close</Button> */}
                {IsUserClient && (
                <button className="btn-primary active-btn-blue search-btn" type="button" 
                    onClick={() => {uploadSignedDocument(selectedFiles);}} 
                    disabled={selectedFiles.length === 0}
                    style={{
                        opacity: selectedFiles.length === 0 ? 0.5 : 1,
                        cursor: selectedFiles.length === 0 ? 'not-allowed' : 'pointer',
                        maxWidth: 'none',
                        minWidth: 'auto',
                        padding: '8.5px 33px',
                        whiteSpace: 'nowrap',
                    }}
                >Send to MISC</button>
                )}
            </DialogActions>
        </Dialog>
    );
};
export default RequestSignature;